import { LoaderMask } from 'components/LoaderMask/LoaderMask';
import { useAsyncEffect } from 'customHooks/hooks';
import React, { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetGeneratorContext } from 'screens/WidgetGenerator/contexts/WidgetGeneratorContext';
import { getCrossTabsData } from 'screens/WidgetGenerator/helpers/crossTabs/crossTabs';
import { DemographicGeographicType } from 'screens/WidgetGenerator/utils/misc';
import { CrossTabs } from 'screens/WidgetGenerator/widgets/CrossTabs/CrossTabs';
import { logger } from 'utils/logger';
import { SurveysReportGeneratorFilterKey } from 'screens/WidgetGenerator/reportGeneratorComponents/enums';

import { ReportBuilderComponentProps } from '../WidgetWrappers/WidgetWrappers';
import { CrossTabsFiltersSelectedData, CrossTabsFiltersType } from './CrossTabsFilters';

export const CrossTabsHOC = (props: ReportBuilderComponentProps): ReactElement => {
  const { params } = props;
  const { filters } = params;
  const crossTabsFilters: CrossTabsFiltersSelectedData = filters[SurveysReportGeneratorFilterKey.CROSS_TABS_FILTERS];
  const { client, cycles, selectedDateRange, selectedSurveyGroup } = useContext(WidgetGeneratorContext);

  const { t: translate } = useTranslation();

  const fetchCrossTabData = async () => {
    if (!crossTabsFilters) {
      return null;
    }
    const mappedBaseGenericQuestionId = crossTabsFilters[CrossTabsFiltersType.BASE_QUESTION];
    const mappedCrossQuestionOptions = crossTabsFilters[CrossTabsFiltersType.CROSS_QUESTIONS];

    if (!mappedBaseGenericQuestionId || !mappedCrossQuestionOptions?.length) {
      return null;
    }
    const selectedBaseGenericQuestionId = mappedBaseGenericQuestionId.value as number;

    const filteredDemographicGeographicTypes = mappedCrossQuestionOptions.reduce<string[]>(
      (filteredQuestions, option) => {
        const optionValue = option.value as string;
        if (Object.keys(DemographicGeographicType).includes(optionValue)) return [...filteredQuestions, optionValue];
        return filteredQuestions;
      },
      [],
    );

    const crossQuestionSelectedOptions = mappedCrossQuestionOptions.filter(
      (option) => !Object.keys(DemographicGeographicType).includes(option.value as string),
    );

    const fetchedTopicsData = await getCrossTabsData({
      client,
      cycles,
      selectedDateRange,
      selectedBaseGenericQuestionId,
      crossQuestionSelectedOptions,
      surveyGroupId: selectedSurveyGroup.id,
      demographicGeographic: filteredDemographicGeographicTypes,
    });

    return fetchedTopicsData;
  };

  const {
    fetchedData: fetchedCrossTabData,
    isLoading,
    error,
  } = useAsyncEffect(fetchCrossTabData, [client, cycles, selectedDateRange, crossTabsFilters, selectedSurveyGroup]);

  if (error) {
    logger.error(error);
  }

  if (!fetchedCrossTabData) {
    return <p>{translate('widgetGenerator.widgets.crossTabs.emptyState')}</p>;
  }

  return (
    <LoaderMask isLoading={isLoading}>
      <CrossTabs widgetData={fetchedCrossTabData} />
    </LoaderMask>
  );
};
